import { addYears } from 'date-fns';
import produce from 'immer';
import { isEqual } from 'lodash';
import Cookies from 'universal-cookie';

import type { Counter, Notification, PSNPartnerNotification } from '@swf/core/types/Notification';

import type { NotificationState } from './index';

const cookies = new Cookies();

const reducers = {
  set: (
    state: NotificationState,
    { name, value }: { name: keyof NotificationState['cookies']; value: boolean }
  ): NotificationState => {
    cookies.set(name, value, { expires: addYears(new Date(), 1), path: '/' });

    return produce(state, (draft) => {
      draft.cookies[name] = value;
    });
  },
  addIssued: (state: NotificationState, payload: Notification): NotificationState =>
    produce(state, (draft) => {
      draft.issued[payload.rollId] = { ...(draft.issued[payload.rollId] || {}), ...payload };
    }),
  addExpiring: (state: NotificationState, payload: Notification): NotificationState =>
    produce(state, (draft) => {
      draft.expiring[payload.rollId] = payload;
    }),
  addCleared: (state: NotificationState, payload: Notification): NotificationState =>
    produce(state, (draft) => {
      draft.cleared[payload.rollId] = payload;
    }),
  addCounter: (state: NotificationState, payload: Counter): NotificationState =>
    produce(state, (draft) => {
      draft.counters = { ...state.counters, ...payload };
    }),
  addPSNPartnerNotification: (
    state: NotificationState,
    payload: PSNPartnerNotification
  ): NotificationState =>
    produce(state, (draft) => {
      const { templateId, templateContext } = payload;
      if (!draft.PSNPartnerNotifications[templateId]) {
        draft.PSNPartnerNotifications[templateId] = [];
      }
      const index = draft.PSNPartnerNotifications[templateId].findIndex((notification) =>
        isEqual(notification.templateContext, templateContext)
      );
      if (index === -1) {
        draft.PSNPartnerNotifications[templateId].push(payload);
      }
    }),
  removePSNPartnerNotification: (
    state: NotificationState,
    payload: PSNPartnerNotification
  ): NotificationState =>
    produce(state, (draft) => {
      const { templateId, templateContext } = payload;
      if (draft.PSNPartnerNotifications[templateId]) {
        const index = draft.PSNPartnerNotifications[templateId].findIndex((notification) =>
          isEqual(notification.templateContext, templateContext)
        );
        if (index !== -1) {
          draft.PSNPartnerNotifications[templateId].splice(index, 1);
          !draft.PSNPartnerNotifications[templateId].length &&
            delete draft.PSNPartnerNotifications[templateId];
        }
      }
    }),
  reset: (state: NotificationState): NotificationState => ({
    ...state,
    issued: {},
    expiring: {},
    cleared: {},
    PSNPartnerNotifications: {}
  })
};

export default reducers;
